import React, { FC } from 'react';

import { Plus, Xmark } from '@gravity-ui/icons';
import { Button, Icon, Select, Text, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { getSettingsLabels } from 'client/blocks/Settings/channels/labels';
import { StructuredExamplesDescription } from 'client/blocks-redesign/descriptions/descriptions';
import { Field } from 'client/blocks-redesign/form/Field/Field';
import { Example, makeEmptyExample, markers } from 'client/model/common';
import { Channel } from 'client/model/skill';
import { ucfirst } from 'client/utils';
import { shouldDisplayRequest } from 'client/utils/settings/alice-skill';
import * as keyset from 'i18n/AppForm';

import styles from './ExamplesBuilder.module.scss';

const t = i18n(keyset);

interface Marker {
    text: string;
    hasRequest: boolean;
}

interface ExamplesBuilderProps {
    disabled?: boolean;
    activationPhrasesOptions: string[];
    values: Example[];
    errors?: string[];
    onChange: (values: Example[]) => void;
}

export const ExamplesBuilder: React.SFC<ExamplesBuilderProps> = ({
    activationPhrasesOptions,
    errors,
    values,
    onChange,
    disabled,
}: ExamplesBuilderProps) => {
    const handleAdd = () => {
        onChange([...values, makeEmptyExample()]);
    };

    const makeHandleChange = (index: number) => (value: Example) => {
        const updatedValues = [...values];
        updatedValues.splice(index, 1, value);
        onChange(updatedValues);
    };

    const makeHandleDelete = (index: number) => () => {
        const updatedValues = [...values];
        updatedValues.splice(index, 1);
        onChange(updatedValues);
    };

    const title = getSettingsLabels()?.[Channel.AliceSkill]?.structuredExamples;

    return (
        <Field fieldTitle={title} fieldDescription={<StructuredExamplesDescription />} required>
            {values.map((value, index) => (
                <ExampleBuilderRow
                    key={index}
                    markers={markers}
                    activationPhrasesOptions={activationPhrasesOptions}
                    onChange={makeHandleChange(index)}
                    onDelete={makeHandleDelete(index)}
                    value={value}
                    disabled={disabled}
                    disableDelete={disabled || index === 0}
                    error={errors && errors[index] ? errors[index] : undefined}
                />
            ))}
            {values.length < 3 ? (
                <Button className={styles.formButton} onClick={handleAdd} disabled={disabled} size="l">
                    <Icon data={Plus} />
                    {t('add')}
                </Button>
            ) : null}
        </Field>
    );
};

interface ExampleBuilderRowProps {
    disabled?: boolean;
    markers: Marker[];
    activationPhrasesOptions: string[];
    value: Example;
    onChange: (value: Example) => void;
    onDelete: () => void;
    disableDelete: boolean;
    error?: string;
}

const ExampleBuilderRow: FC<ExampleBuilderRowProps> = ({
    value,
    disabled,
    markers,
    activationPhrasesOptions,
    onChange,
    onDelete,
    disableDelete,
    error,
}) => {
    const displayRequestField = shouldDisplayRequest(value.marker);

    return (
        <>
            <div className={clx(styles.exampleRow, displayRequestField && styles.exampleRow_withRequest)}>
                <Select
                    placeholder="Команда"
                    value={[value.marker]}
                    onUpdate={(val) => {
                        onChange({
                            ...value,
                            marker: val[0],
                            request: shouldDisplayRequest(val[0]) ? value.request : '',
                        });
                    }}
                    options={markers.map((marker) => ({ value: marker.text, content: ucfirst(marker.text) }))}
                    disabled={disabled}
                    size="xl"
                />
                <Select
                    placeholder="Имя"
                    value={[value.activationPhrase]}
                    onUpdate={(val) =>
                        onChange({
                            ...value,
                            activationPhrase: val[0],
                        })
                    }
                    options={activationPhrasesOptions.map((phrase) => ({ value: phrase, content: phrase }))}
                    disabled={disabled}
                    size="xl"
                />
                {displayRequestField && (
                    <TextInput
                        placeholder="Запрос"
                        value={value.request}
                        onUpdate={(val) =>
                            onChange({
                                ...value,
                                request: val,
                            })
                        }
                        disabled={disabled}
                        size="xl"
                    />
                )}
                <Button onClick={onDelete} disabled={disableDelete} size="xl">
                    <Icon data={Xmark} />
                </Button>
            </div>
            {error ? (
                <div>
                    <Text color="danger">{error}</Text>
                </div>
            ) : null}
        </>
    );
};
